import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { isSiteTab, paths, SITE_TABS, type SiteTab } from '@/app/paths';
import { Button, Card, Icon, Lifecycle, Pill, Segmented, Tag, useToast, type IconName } from '@/design-system';
import { useSession } from '@/app/session';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { LoadError } from '@/shell/LoadError';
import { activeDeployment, deploymentsQuery } from '@/features/deploy/api';
import { PublishModal, useDemoHost, usePublishWatch } from '@/features/deploy/PublishModal';
import { demoOf, domainsQuery } from '@/features/domains/api';
import { websiteQuery, type Website } from './api';
import { landingTab, LIFECYCLE, STATUS_TONE } from './status';
import s from './sites.module.css';

const TAB_ICONS: Record<SiteTab, IconName> = {
  overview: 'layout-panel-left',
  research: 'scan-search',
  structure: 'layers',
  variants: 'copy',
  designer: 'sparkles',
  sync: 'refresh-cw',
  domains: 'globe',
  deploy: 'rocket',
  analytics: 'mouse-pointer-click',
};

/** The phase that builds each tab (planning/ROADMAP.md). */
const TAB_PHASE: Partial<Record<SiteTab, string>> = {
  research: 'P2',
  structure: 'P4',
  variants: 'P4',
  designer: 'P4',
};

/** /sites/:siteId/* — the site workspace frame: header card, lifecycle, the 8 tabs. */
export function SiteLayout() {
  const { siteId = '' } = useParams();
  // Child routes are partly static (`overview`), so the tab comes from the path, not a param.
  const tab = useLocation().pathname.split('/')[3];
  const { t } = useTranslation('sites');
  const { t: ts } = useTranslation('shell');
  const navigate = useNavigate();
  const q = useQuery(websiteQuery(siteId));
  const site = q.data;
  useBreadcrumbs(
    site
      ? [
          { label: ts('nav.organizations'), to: paths.organizations() },
          { label: site.organization.brand_name, to: paths.organization(site.organization.id, 'sites') },
          { label: site.name },
        ]
      : [{ label: ts('nav.organizations'), to: paths.organizations() }],
  );
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!site) return null;
  const current = LIFECYCLE.indexOf(site.status as (typeof LIFECYCLE)[number]);
  return (
    <>
      <Card texture="dots" padding="none" className={s.head}>
        <div className={s.headRow}>
          <div className={s.headMain}>
            <div className={s.chips}>
              <Tag>{t(`type.${site.type}`)}</Tag>
              <Pill tone={STATUS_TONE[site.status]}>{t(`status.${site.status}`)}</Pill>
              {site.domain && <span className={s.headDomain}>{site.domain}</span>}
            </div>
            <h1 className={s.siteTitle}>{site.name}</h1>
          </div>
          <SiteActions site={site} />
        </div>
        <Lifecycle label={t('life.label')} current={current} steps={LIFECYCLE.map((k) => ({ key: k, label: t(`life.${k}`) }))} />
      </Card>
      <Segmented
        variant="tabs"
        label={t('tabs_label')}
        value={isSiteTab(tab) ? tab : 'overview'}
        onChange={(v) => navigate(paths.site(site.id, v))}
        options={SITE_TABS.map((k) => ({ value: k, label: t(`tabs.${k}`), icon: TAB_ICONS[k] }))}
      />
      <Outlet context={site} />
    </>
  );
}

/**
 * «Открыть демо» (the active demo in a new tab; the toast says noindex and auth) and «Опубликовать» (the
 * publish modal, P5-E01). The header follows the publications from every tab, so the lifecycle moves live.
 */
function SiteActions({ site }: { site: Website }) {
  const { t } = useTranslation('sites');
  const { t: td } = useTranslation('deploy');
  const { can } = useSession();
  const toast = useToast();
  const [publishing, setPublishing] = useState(false);
  const deployments = useQuery(deploymentsQuery(site.id));
  const host = useDemoHost(site);
  const domains = useQuery(domainsQuery(site.id));
  usePublishWatch(site);
  const live = activeDeployment(deployments.data);
  const canPublish = can('deployment:publish_demo');
  const openDemo = () => {
    if (!live || !host) {
      toast(td('demo.none'));
      return;
    }
    // D22: with the access link on, the demo opens through it (`demo.access.url`); the toast says auth then.
    const access = demoOf(domains.data)?.access;
    const url = access?.required && access.url ? access.url : `https://${host}/`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast(access?.required ? td('demo.opened_auth', { domain: host }) : td('demo.opened', { domain: host }));
  };
  return (
    <div className={s.actions}>
      <Button variant="secondary" icon="external-link" iconSize={15} onClick={openDemo}>
        {t('actions.open_demo')}
      </Button>
      <Button
        icon="rocket"
        aria-disabled={!canPublish || undefined}
        title={canPublish ? undefined : td('publish.forbidden')}
        onClick={() => (canPublish ? setPublishing(true) : toast(td('publish.forbidden')))}
      >
        {t('actions.publish')}
      </Button>
      <PublishModal site={site} open={publishing} onClose={() => setPublishing(false)} />
    </div>
  );
}

/** /sites/:siteId: a site in design opens on AI Designer, any other on Обзор. */
export function SiteIndexRedirect() {
  const { siteId = '' } = useParams();
  const q = useQuery(websiteQuery(siteId));
  if (!q.data) return null;
  return <Navigate to={paths.site(siteId, landingTab(q.data))} replace />;
}

/** Tabs whose screens later phases build: the frame is live, the content says when it arrives. */
export function PendingSiteTab() {
  const { tab } = useParams();
  const { t } = useTranslation('sites');
  if (!isSiteTab(tab)) return <Navigate to="../overview" replace relative="path" />;
  const phase = TAB_PHASE[tab];
  if (!phase) return null;
  return (
    <Card className={s.pending}>
      <Icon name={TAB_ICONS[tab]} size={24} className={s.pendingIcon} />
      <span className={s.pendingTitle}>{t('pending_tab.title', { tab: t(`tabs.${tab}`), phase })}</span>
      <span className={s.pendingText}>{t('pending_tab.text')}</span>
    </Card>
  );
}
